import React, { useState, useMemo } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Collapse,
  Button,
} from "reactstrap"
import Switch from "react-switch"

const GroupedPermissionSelector = ({
  groupedPermissions,
  selectedPermissions,
  onChange,
  disabledPermissions = [],
}) => {
  const [expandedGroups, setExpandedGroups] = useState({})
  const [searchTerm, setSearchTerm] = useState("")

  // Toggle expand/collapse for a group
  const toggleGroup = resource => {
    setExpandedGroups(prev => ({
      ...prev,
      [resource]: !prev[resource],
    }))
  }

  // Toggle all groups
  const toggleAllGroups = expand => {
    const newState = {}
    filteredGroups.forEach(group => {
      newState[group.resource] = expand
    })
    setExpandedGroups(newState)
  }

  // Check if permission is selected
  const isSelected = permissionName => {
    return selectedPermissions.includes(permissionName)
  }

  // Check if permission is disabled
  const isDisabled = permissionName => {
    return disabledPermissions.includes(permissionName)
  }

  // Toggle single permission
  const togglePermission = permissionName => {
    if (isDisabled(permissionName)) return

    if (isSelected(permissionName)) {
      onChange(selectedPermissions.filter(p => p !== permissionName))
    } else {
      onChange([...selectedPermissions, permissionName])
    }
  }

  // Toggle all permissions in a group
  const toggleGroupPermissions = group => {
    const groupPermissionNames = group.permissions.map(p => p.value || p.name)
    const enabledGroupPermissions = groupPermissionNames.filter(
      p => !isDisabled(p)
    )
    const allSelected = enabledGroupPermissions.every(p => isSelected(p))

    if (allSelected) {
      // Deselect all
      onChange(
        selectedPermissions.filter(p => !groupPermissionNames.includes(p))
      )
    } else {
      // Select all enabled
      const newSelected = [...selectedPermissions]
      enabledGroupPermissions.forEach(p => {
        if (!newSelected.includes(p)) {
          newSelected.push(p)
        }
      })
      onChange(newSelected)
    }
  }

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!groupedPermissions || groupedPermissions.length === 0) return []
    if (!searchTerm) return groupedPermissions

    return groupedPermissions
      .map(group => ({
        ...group,
        permissions: group.permissions.filter(
          p =>
            p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }))
      .filter(group => group.permissions.length > 0)
  }, [groupedPermissions, searchTerm])

  const getActionBadgeColor = action => {
    switch (action) {
      case "show":
        return "info"
      case "create":
        return "success"
      case "update":
        return "warning"
      case "delete":
        return "danger"
      case "manage":
        return "primary"
      default:
        return "secondary"
    }
  }

  const Offsymbol = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 12,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      No
    </div>
  )

  const OnSymbol = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 12,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      Yes
    </div>
  )

  if (!groupedPermissions || groupedPermissions.length === 0) {
    return (
      <div className="alert alert-info">
        No permissions available. Please add permissions first.
      </div>
    )
  }

  return (
    <div>
      {/* Search and Controls */}
      <Row className="mb-3">
        <Col md={6}>
          <Input
            type="text"
            placeholder="Search permissions or resources..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <Button
            size="sm"
            color="primary"
            outline
            className="me-2"
            onClick={() => toggleAllGroups(true)}
          >
            Expand All
          </Button>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => toggleAllGroups(false)}
          >
            Collapse All
          </Button>
        </Col>
      </Row>

      {/* Grouped Permissions */}
      {filteredGroups.length === 0 ? (
        <div className="alert alert-warning">
          No permissions match your search.
        </div>
      ) : (
        filteredGroups.map((group, idx) => {
          const groupPermissionNames = group.permissions.map(
            p => p.value || p.name
          )
          const enabledGroupPermissions = groupPermissionNames.filter(
            p => !isDisabled(p)
          )
          const selectedCount = groupPermissionNames.filter(p =>
            isSelected(p)
          ).length
          const isGroupExpanded = expandedGroups[group.resource]

          return (
            <Card key={idx} className="mb-2">
              <CardHeader
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => toggleGroup(group.resource)}
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`bx ${
                      isGroupExpanded ? "bx-chevron-down" : "bx-chevron-right"
                    } me-2`}
                  ></i>
                  <h6 className="mb-0">
                    <i className="bx bx-lock-alt me-2"></i>
                    {group.display_name || group.resource}
                  </h6>
                  <Badge color="primary" className="ms-2">
                    {selectedCount}/{group.permissions.length}
                  </Badge>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <Label className="form-label mb-0 me-2">Select All</Label>
                  <Input
                    type="checkbox"
                    checked={
                      enabledGroupPermissions.length > 0 &&
                      enabledGroupPermissions.every(p => isSelected(p))
                    }
                    onChange={() => toggleGroupPermissions(group)}
                    disabled={enabledGroupPermissions.length === 0}
                  />
                </div>
              </CardHeader>
              <Collapse isOpen={isGroupExpanded}>
                <CardBody>
                  <Row>
                    {group.permissions.map((permission, permIdx) => {
                      const permissionName = permission.value || permission.name
                      const action = permission.action || ""
                      const disabled = isDisabled(permissionName)

                      return (
                        <Col md={6} lg={4} key={permIdx} className="mb-3">
                          <div
                            className={`d-flex justify-content-between align-items-center p-2 border rounded ${
                              disabled ? "bg-light" : ""
                            }`}
                          >
                            <div>
                              <code className="text-muted small">
                                {permissionName}
                              </code>
                              <br />
                              <Badge
                                color={getActionBadgeColor(action)}
                                className="mt-1"
                              >
                                {action}
                              </Badge>
                            </div>
                            <Switch
                              uncheckedIcon={<Offsymbol />}
                              checkedIcon={<OnSymbol />}
                              onColor="#626ed4"
                              onChange={() => togglePermission(permissionName)}
                              checked={isSelected(permissionName)}
                              disabled={disabled}
                              height={20}
                              width={48}
                            />
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          )
        })
      )}
    </div>
  )
}

export default GroupedPermissionSelector
